import { useEffect, useState, useRef } from 'react';
import { onValue, ref, update } from 'firebase/database';
import { realtimeDatabase } from 'firebaseConfig';

export type TimeSlotType = {
    availableCount: number;
    lastUpdated: number;
};

export type DateSlotType = {
    [time: string]: TimeSlotType;
};

export type AvailableSlotType = {
    [date: string]: DateSlotType;
};

const useAvailableSlots = (hospitalId?: string) => {
    const [availableSlots, setAvailableSlots] = useState<AvailableSlotType>({});
    const [isLoading, setIsLoading] = useState(true);
    const unsubscribeRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!hospitalId) {
            setIsLoading(false);
            return;
        }

        // 이전 리스너 정리
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }

        const slotsRef = ref(realtimeDatabase, `availableSlotCount/${hospitalId}`);

        const unsubscribe = onValue(
            slotsRef,
            (snapshot) => {
                const data = snapshot.val();

                if (data) {
                    setAvailableSlots((prev) => ({
                        ...prev,
                        ...data,
                    }));
                }
                setIsLoading(false);
            },
            (error) => {
                console.error('Error in Firebase listener:', error);
                setIsLoading(false);
            }
        );

        unsubscribeRef.current = unsubscribe;

        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [hospitalId]);

    const updateSlotCount = async (date: string, time: string, hospitalId: string) => {
        try {
            const currentCount = availableSlots[date]?.[time]?.availableCount ?? 0;

            if (currentCount <= 0) {
                console.error('No available slots to update');
                return false;
            }

            const updates = {
                [`availableSlotCount/${hospitalId}/${date}/${time}/availableCount`]: currentCount - 1,
                [`availableSlotCount/${hospitalId}/${date}/${time}/lastUpdated`]: Date.now(),
            };

            await update(ref(realtimeDatabase), updates);
            return true;
        } catch (error) {
            console.error('Error updating slot count:', error);
            return false;
        }
    };

    return { availableSlots, isLoading, updateSlotCount };
};

export default useAvailableSlots;
